import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChartBar, Plus, X, Trash, LockSimple, Check } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useDialog } from "../components/Dialog";

export default function Polls() {
  const { user } = useAuth();
  const isStaff = ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user?.role);
  const { confirm } = useDialog();
  const [polls, setPolls] = useState([]);
  const [add, setAdd] = useState(false);

  const load = useCallback(() => { api.get("/api/polls").then(({ data }) => setPolls(data.items || [])).catch(() => {}); }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  async function vote(poll, optId) {
    if (poll.status !== "OPEN") return;
    const my = new Set(poll.myVote || []);
    let next;
    if (poll.multi) { my.has(optId) ? my.delete(optId) : my.add(optId); next = [...my]; }
    else next = [optId];
    const { data } = await api.post(`/api/polls/${poll.id}/vote`, { optionIds: next });
    setPolls((ps) => ps.map((p) => (p.id === poll.id ? data : p)));
  }
  async function close(p) { await api.post(`/api/polls/${p.id}/close`); load(); }
  async function del(p) {
    if (!(await confirm({ title: "Delete poll?", message: p.question, confirmText: "Delete", danger: true }))) return;
    await api.delete(`/api/polls/${p.id}`); load();
  }

  return (
    <div className="space-y-5 font-sans" data-testid="polls-screen">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/50">Community</p>
          <h1 className="font-display text-3xl font-extrabold text-white">Polls</h1>
        </div>
        {isStaff && <button onClick={() => setAdd(true)} data-testid="poll-add" className="inline-flex items-center gap-2 rounded-2xl bg-neon-cyan px-4 py-2.5 text-sm font-bold text-black shadow-glow-cyan"><Plus size={16} weight="bold" /> New poll</button>}
      </div>

      {polls.length === 0 ? <div className="card p-8 text-center text-sm text-white/45">No polls right now.</div>
        : polls.map((p) => {
          const total = p.totalVoters || 0;
          const voted = (p.myVote || []).length > 0;
          const open = p.status === "OPEN";
          return (
            <div key={p.id} className="card p-5" data-testid={`poll-${p.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display text-[16px] font-bold text-white">{p.question}</p>
                  <p className="mt-0.5 text-[11px] text-white/45">{total} vote{total === 1 ? "" : "s"} · {open ? (p.multi ? "Pick one or more" : "Pick one") : "Closed"}{!open ? "" : voted ? " · you voted" : ""}</p>
                </div>
                {isStaff && (
                  <div className="flex shrink-0 gap-1">
                    {open && <button onClick={() => close(p)} title="Close" className="rounded-lg p-1.5 text-white/40 hover:text-neon-amber"><LockSimple size={15} weight="bold" /></button>}
                    <button onClick={() => del(p)} title="Delete" className="rounded-lg p-1.5 text-white/40 hover:text-neon-pink"><Trash size={15} weight="bold" /></button>
                  </div>
                )}
              </div>
              <div className="mt-3 space-y-2">
                {p.options.map((o) => {
                  const pct = total > 0 ? Math.round((o.votes / total) * 100) : 0;
                  const mine = (p.myVote || []).includes(o.id);
                  return (
                    <button key={o.id} onClick={() => vote(p, o.id)} disabled={!open} data-testid={`poll-opt-${o.id}`}
                      className={`relative w-full overflow-hidden rounded-2xl border p-3 text-left transition ${mine ? "border-neon-cyan/60" : "border-white/10"} ${open ? "hover:border-white/25" : "cursor-default"}`}>
                      <div className="absolute inset-0 bg-neon-cyan/10" style={{ width: `${pct}%` }} />
                      <div className="relative flex items-center justify-between">
                        <span className="flex items-center gap-2 text-[14px] font-semibold text-white">{mine && <Check size={15} weight="bold" className="text-neon-cyan" />}{o.text}</span>
                        <span className="text-[13px] font-mono text-white/70">{pct}% · {o.votes}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

      <AnimatePresence>{add && <PollSheet onClose={() => setAdd(false)} onCreated={() => { setAdd(false); load(); }} />}</AnimatePresence>
    </div>
  );
}

function PollSheet({ onClose, onCreated }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const valid = question.trim().length >= 3 && options.filter((o) => o.trim()).length >= 2;
  function setOpt(i, v) { setOptions((a) => a.map((o, j) => (j === i ? v : o))); }
  async function submit() {
    if (!valid) { setErr("Add a question and at least 2 options."); return; }
    setBusy(true); setErr(null);
    try { await api.post("/api/polls", { question: question.trim(), options: options.map((o) => o.trim()).filter(Boolean), multi }); onCreated(); }
    catch { setErr("Could not create."); setBusy(false); }
  }
  return (
    <motion.div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        className="relative w-full max-w-md overflow-y-auto rounded-t-[2rem] border border-white/10 bg-ink-900 p-6 pb-safe sm:rounded-[2rem]" data-testid="poll-sheet">
        <div className="flex items-center justify-between"><h3 className="font-display text-xl font-bold text-white">New poll</h3><button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button></div>
        <div className="mt-4 space-y-4">
          <div><label className="label">Question</label><input className="field" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. Should we repaint the lobby?" data-testid="poll-q" /></div>
          <div>
            <label className="label">Options</label>
            <div className="space-y-2">
              {options.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <input className="field" value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} data-testid={`poll-opt-input-${i}`} />
                  {options.length > 2 && <button onClick={() => setOptions((a) => a.filter((_, j) => j !== i))} className="rounded-xl bg-white/5 px-3 text-white/50"><X size={14} weight="bold" /></button>}
                </div>
              ))}
              {options.length < 8 && <button onClick={() => setOptions((a) => [...a, ""])} className="text-[12px] font-bold text-neon-cyan">+ Add option</button>}
            </div>
          </div>
          <button onClick={() => setMulti((v) => !v)} className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
            <span className="text-[13px] font-semibold text-white">Allow multiple choices</span>
            <span className={`flex h-6 w-11 items-center rounded-full px-0.5 ${multi ? "bg-neon-cyan" : "bg-white/15"}`}><span className={`h-5 w-5 rounded-full bg-white transition-transform ${multi ? "translate-x-5" : ""}`} /></span>
          </button>
          {err && <p className="rounded-xl border border-neon-pink/30 bg-neon-pink/10 px-3 py-2 text-sm text-neon-pink">{err}</p>}
          <button disabled={!valid || busy} onClick={submit} data-testid="poll-submit" className="btn-primary w-full disabled:opacity-50">{busy ? "Posting…" : "Post poll"}</button>
        </div>
      </motion.div>
    </motion.div>
  );
}
